import React, { useRef, useState, useEffect } from 'react';
import { injectIntl, FormattedMessage, FormattedPlural } from 'react-intl';
import { getPromotionMainColor, getPromotionTextColor, i18nIntl } from '../../lib/ep.js';

import Spinner from '../../components/builder/Spinner';
import SaveButton from '../../components/builder/SaveButton';

import FloatingLayerPublic from '../../components/front/FloatingLayerPublic';
import PromoColsWrapper from '../../components/front/PromoColsWrapper';

import RevealWinCanvas from '../../components/games/RevealWinCanvas.js';
import TryIconAnimated from '../../components/svg/TryIconAnimated.js';
import ParentControlled from '../../components/ParentControlled.js';

const standard_board_size = 600;	// px

const LiveCounter = ({ num_tries, spent_tries, is_winner, try_status, color, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const total_w = props?.total_w ?? standard_board_size;
	const slots = [];
	if(num_tries != undefined) {
		for(let i = 0; i < num_tries; i++) slots.push(i + 1);
	}

	if(slots.length < 2) return null;

	const pending_tries = num_tries - spent_tries;
	const reveal_num = props?.reveal_num || 3;

	return (
		<>
			{(reveal_num == 3) && (
				<div class="reveal_instructions gui_main_text_color_on_bg">
					<p class="gui_text_block">
						<span>{i18n("Find 3 of a kind.")}</span>

						<strong class="gui_not_in_start_tiny">
							{(pending_tries > 0) ? (
								<FormattedPlural value={pending_tries} one={<FormattedMessage id="You have {num} attempt." values={{num:pending_tries}} />} other={<FormattedMessage id="You have {num} attempts." values={{num:pending_tries}} />} />
							) : (
								<span>{i18n("You ran out of moves!")}</span>
							)}
						</strong>
					</p>
				</div>
			)}

			<ul class="num_tries">
				{slots.map((slot, i) => {
					const is_spent = (i < spent_tries) ? true : false;
					const is_last_item = ((spent_tries == num_tries - 1) && (i == slots.length - 1)) ? true : false;
					const is_spent_ok = (try_status[i] != undefined) ? try_status[i] : null;
					const li_styles = (is_spent_ok != null) ? { background:((is_spent_ok) ? props.color_ok : props.color_ko) } : null;

					return (
						<li key={`try_${i}`} style={li_styles}>
							<TryIconAnimated is_spent={is_spent} last_item={is_last_item && !is_winner} is_winner={is_spent_ok} color={color} icon_w={(total_w - 5 * (slots.length - 1)) / slots.length} />
						</li>
					)
				})}
			</ul>
		</>
	)
};

const BGReveal = ({ bg_type, stage, main_color }) => {
	const game_bg = (stage.bg_image != undefined) ? stage.full_bg_image : false;
	const game_bg_ok = (stage.bg_image_ok != undefined) ? stage.full_bg_image_ok : false;
	const game_bg_ko = (stage.bg_image_ko != undefined) ? stage.full_bg_image_ko : false;
	const game_bg_color = stage?.bg_color || main_color;

	let bg_styles = {};
	if(game_bg) bg_styles.backgroundImage = `url(${game_bg})`;
	if(game_bg_color) bg_styles.backgroundColor = game_bg_color;
	switch(bg_type) {
		case -1:
			break;

		case 0:
			if(game_bg_ko) bg_styles.backgroundImage = `url(${game_bg_ko})`;
			break;

		case 1:
			if(game_bg_ok) bg_styles.backgroundImage = `url(${game_bg_ok})`;
			break;
	}

	return (
		<div class="game_bg_mod is_fullscreen relative_to_container" style={bg_styles} />
	)
};

export const InstructionsLayer = ({ stage, promo, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const closeLayer = (cb) => {
		if(props.participate != undefined) props.participate(cb);
	}

	const is_builder = props?.is_builder ?? false;

	let description = props.intl.formatMessage({ id:"You have {tries} tries to find {num_prizes} prizes" }, { tries:stage.reveal_tries, num_prizes:stage.reveal_num });
	if(stage.instructions_description != undefined && stage.instructions_description != "") description = stage.instructions_description;

	return (
		<FloatingLayerPublic
			pm={promo}
			title={<><h1>{stage?.instructions_title ?? i18n("How to play?")}</h1><p class="instructions_text" dangerouslySetInnerHTML={{ __html:description.replace(/\n/g, '<br />') }} /></>}
			//backButton={(e, cb) => closeLayer(cb)}
			is_builder={is_builder}
			display_close_button={false}
			enable_close={false}
			max_width='350px'
		>
			<div class="gui_canvas_instructions">
				<div class="cta_and_info">
					<div class="gui_not_in_top_small flex_button gui_big_button register_flex">
						<SaveButton
							_fullclass=""
							onClick={(e, cb) => closeLayer(cb)}
							autotranslate={false}
						>{stage?.instructions_button ?? i18n("Try your luck")}</SaveButton>
					</div>
				</div>

				{(stage.instructions_img) && (
					<div class="gui_text_align_center gui_pos_rel" style={{minHeight:'50px'}}>
						<img class="instructions_img" src={stage.full_instructions_img} alt="" />
					</div>
				)}
			</div>
		</FloatingLayerPublic>
	)
};

const RevealStage = (props) => {
	const stage = (props.stage != undefined) ? props.stage.Stage : false;
	if(stage === false) return null;

	const is_builder = props?.is_builder ?? false;
	const is_editing = (is_builder) ? (props?.is_editing ?? false) : false;
	const is_playable = props?.is_playable ?? true;
	const display_instructions = props?.display_instructions ?? false;

	const [displayFirstTry, setDisplayFirstTry] = useState(!is_builder);
	const [response, setResponse] = useState(props.userInfo ?? {});
	const [given_prize, setGivenPrize] = useState(props.given_prize ?? null);
	const [canvas_size, setCanvasSize] = useState(null);
	const [participating, setParticipating] = useState(null);

	const running = useRef(true);
	const currentuid = useRef();
	const live_counter = useRef();
	const bg_img = useRef();

	const promo = props.promotion.Promotion;

	const getCanvasSize = (num_tries) => {
		const container = document.querySelector('.gui_game_parent');
		while(num_tries > 0 && container == undefined) getCanvasSize(num_tries - 1);

		if(container != undefined) {
			const calculated_size = Math.min(container.clientWidth, document.querySelector('.flex_cols').clientWidth);
			if(calculated_size > 250) return calculated_size;
		}

		return standard_board_size;
	};

	useEffect(() => {
		setTimeout(() => {
			setCanvasSize(getCanvasSize(3));
		}, 400);

		const _container = document.querySelector('.main_flex_wrapper');
		_container.classList.remove('is_game');
		_container.classList.add('is_new_game');
	}, []);

	const participate = (cb) => {
		if(is_builder || participating) return;
		setParticipating(true);
		$.post(`/promotions/participate/${promo.id}/${stage.id}`, { spin:true, ticket:props.ticket }, (res) => {
			if(res.status) {
				let parts = res.url.split('/');
				currentuid.current = parts[parts.length - 2];
				setGivenPrize(res.spt);

				setResponse(res);
				if(displayFirstTry) setDisplayFirstTry(false);
				if(cb != undefined) cb();
			} else {
				if(res.url != undefined) window.location.href = res.url;
			}
			setParticipating(false);
		}, 'JSON');
	};

	const getPrize = (id) => {
		if(id == 0 || id == undefined) return { id:0 };
		if(id == 'prize') return { id:'prize' };
		const prize = props.prizes.find((p) => p.id == id);
		return prize;
	};

	const liveCounterReducer = (state, action) => {
		switch(action.type) {
			case 'SPEND_LIFE':
				return { ...state, spent_tries:state.spent_tries + 1, try_status:[...state.try_status, action.try_status] };

			case 'IS_WINNER':
				return { ...state, spent_tries:state.spent_tries + 1, is_winner:action.is_winner, try_status:[...state.try_status, true] };

			default:
				break;
		}
	};

	const bgReducer = (state, action) => {
		switch(action.type) {
			case 'SET_BG_OK':
				if(!state.stage.bg_image_ok) return state;
				return { ...state, bg_type:1 };

			case 'SET_BG_KO':
				if(!state.stage.bg_image_ko) return state;
				return { ...state, bg_type:0 };

			default:
				break;
		}
	};

	const onHit = (try_status) => {
		live_counter.current.dispatch({ type:'SPEND_LIFE', try_status });
	};

	useEffect(() => {
		setGivenPrize(props.given_prize);
	}, [props.given_prize]);

	const prize = getPrize(given_prize);

	const finishGame = () => {
		running.current = false;
		if(prize.id != 0) live_counter.current.dispatch({ type:'IS_WINNER', is_winner:true });
		bg_img.current.dispatch({ type:((prize.id != 0) ? 'SET_BG_OK' : 'SET_BG_KO') });
	};

	const redirectToSuccess = () => {
		if(response.url != undefined) window.location.href = response.url;
	};

	const main_color = getPromotionMainColor(promo);
	const text_color = getPromotionTextColor(promo);

	return (
		<PromoColsWrapper>
			<ParentControlled
				ref={live_counter}
				initial={{
					num_tries:parseInt(stage.reveal_tries),
					spent_tries:0,
					is_winner:false,
					try_status:[],
					color:text_color,
					color_ok:stage?.game_color_ok || '#75C400',
					color_ko:stage?.game_color_ko || '#D8253B',
					reveal_num:parseInt(stage.reveal_num),
					intl:props.intl,
				}}
				reducer={liveCounterReducer}
			>
				<LiveCounter total_w={canvas_size} />
			</ParentControlled>

			<div class="gui_game_parent">
				<img src={`${window.cdn_url}img/blank_1x1.png`} width="100%" alt="" />

				<ParentControlled
					ref={bg_img}
					initial={{
						bg_type:-1,
						stage,
						main_color,
					}}
					reducer={bgReducer}
				>
					<BGReveal />
				</ParentControlled>

				{(canvas_size != null) ? (
					<RevealWinCanvas
						canvas_size={canvas_size}
						onFinish={finishGame}
						prizeType={prize}
						promotion={promo}
						stage={stage}
						areaChanged={props.areaChanged}
						onHit={onHit}
						userInfo={response}
						onFinishGame={redirectToSuccess}
						is_builder={is_builder}
						is_editing={is_editing}
						is_playable={is_playable}
					/>
				) : (
					<div class="is_fullscreen relative_to_container">
						<Spinner force_bg={true} />
					</div>
				)}

				{(displayFirstTry || display_instructions) && (
					<InstructionsLayer
						participate={participate}
						stage={stage}
						promo={promo}
						is_builder={is_builder}
						intl={props.intl}
					/>
				)}
			</div>
		</PromoColsWrapper>
	);
}

export default injectIntl(RevealStage);